import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, ActionCreator, select, Store} from '@ngrx/store';
import {catchError, exhaustMap, filter, map, mergeMap, switchMap, take, toArray, withLatestFrom} from 'rxjs/operators';
import {addDoc, collection as fsCollection, deleteField, doc as fsDoc, DocumentReference, Firestore, getDoc, onSnapshot, setDoc} from '@angular/fire/firestore';
import {Auth} from '@angular/fire/auth';
import * as UserSelectors from '../../user-access/user-store/user.selectors';
import * as StoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import * as CoreActions from '../../core/store-shared/shared.actions';
import * as SettingSelectors from './setting.selectors';
import {createUserPaths} from '../../../shared/shared-services/database-paths.user';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {logConsoleError} from '../../../shared/shared-module-ui/utils/logger/logger';
import {forkJoin, from, Observable, of, race, timer} from 'rxjs';
import {SettingsService} from '../services';
import {
  initSettings,
  settingGlobalTagsSettings,
  settingSharedLineColours,
  settingsSetSelected,
  settingStockItemEnableRules,
  settingStoreAOExpirationSettings,
  settingStoreAOStoreOpenOrderSettings,
  settingStoreAOStoreOrderScheduler,
  settingStoreAOSupplierEmails,
  settingStoreCopySettings,
  settingStoreOverrides,
  settingStoreProcessedOrder,
  settingStoreMultiplePriceChangeRules,
  settingStorePriceBands,
  settingStoreStockEditThresholdRules,
  settingStoreStockItemAddRule,
  settingStoreTagsSettings,
  settingUserDateFormatting,
  settingUserEditPreviewColsNGPReport,
  settingUserEditPreviewColsStock,
  settingUserFitToGrid,
  settingUserStoreDisplayOrder,
  settingUserStoreMailerConfiguration,
  settingUserStoreOrderConfiguration,
  settingUserTablePreferences,
} from './setting.actions';
import {
  _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS,
  IAddItemRule,
  IAutoOrderAccountSettings,
  IAutoOrderExpirationSettings,
  IAutoOrderOrderingSettings,
  IDisableStockItemRule,
  IEmailSettings,
  IEmailTester,
  IEnableStockItemRule,
  IGlobalStockTag, IOnHandDisableRule,
  IMultiplePriceChangeRulesDocument,
  IMultiplePriceChangeRulesStoreSettings,
  IOpenOrderSettings,
  IPriceBandingStoreSettings,
  IStockItemEditThresholds,
  IStoreOverrideSettings,
  ITableNavSettings,
  sanitizeDisableStockItemRuleForFirestore,
  SettingsReferenceEnum,
  TLineColour,
  TLocaleDateFormat,
  TStockTag,
  TStoreDisplayOrder,
  TSupplierEmail
} from '../models';
import {IProcessedOrderSettings} from '../models/auto-ordering/ao-processed-order-settings.interface';
import {createStorePaths} from '../../../shared/shared-services/database-paths.stores';
import {createGlobalPaths} from '../../../shared/shared-services/database-paths.global';
import {IStore} from '../../../shared/shared-models/store/store';
import {IUser} from '../../user-access/models';
import {generateHexId} from '../../user-access/utils/hex-id-generator.utils';
import {setUserAccessSettingsDefaults} from '../../user-access/user-access-store/user-access.actions';
import {generateGlobalError} from '../../../shared/utils/error/error.utils';
import {IPriceBand} from '../../../shared/shared-models/price-banding/price-band';
import {normalizePriceBandsDocument, toPriceBandsDocument} from '../../../shared/shared-utils/price/price-banding.utils';
import {
  normalizeMultiplePriceChangeRulesDocument,
  toMultiplePriceChangeRulesDocument,
} from '../../../shared/shared-utils/price/multiple-price-change.utils';
import {setGlobalErrorOccurrence} from '../../notifications/store/notification.actions';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {path_admin_cloud_emailer_email_test} from '../../../shared/shared-services/database-paths';
import {CollectionUsersSettingsService, IMailerSettingsPresence} from '../../../shared/shared-services/firebase/collection-users-settings.service';
import {SharedModalUtils} from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {MICROSOFT_MAIL_WINDOW_TIMEOUT_MS, MicrosoftMailService} from '../services/microsoft-mail.service';
import {
  applyConnectedMailbox,
  applyDisconnectedAccount,
  emailTestFailureCopy,
  MAIL_AUTH_MICROSOFT_365,
  MAIL_AUTH_SMTP,
  overlayMicrosoftAccount,
  sanitizeMailerSettings,
} from '../models/email/microsoft-mail.utils';
import {IMicrosoftMailPopupResult} from '../services/microsoft-mail.service';


@Injectable()
export class SettingEffects {

  //#region INJECTORS
  private actions$ = inject(Actions);
  private settingService = inject(SettingsService);
  private readonly _store = inject(Store);
  private readonly _collectionUsersSettingsService = inject(CollectionUsersSettingsService);
  private readonly _firestore = inject(Firestore);
  private readonly _modalUtils = inject(SharedModalUtils);
  private readonly _auth = inject(Auth);
  private readonly _microsoftMail = inject(MicrosoftMailService);
  //#endregion

  //#region [Settings | Initialize] Initialize

  /**
   * Initializes all settings by dispatching actions to load user and store settings.
   * Coordinates multiple concurrent setting fetches and waits for all to complete.
   * @effect start$
   * @listens initSettings.start$
   * @dispatches setUserAccessSettingsDefaults.setDefaultSettings
   * @dispatches settingUserDateFormatting.getDateFormatting
   * @dispatches settingUserStoreDisplayOrder.getStoreDisplayOrder
   * @dispatches settingStoreStockItemAddRule.getAddItemRules
   * @dispatches settingStoreAOSupplierEmails.getAutoOrderSupplierEmails
   * @dispatches settingStoreAOExpirationSettings.getAOExpiration
   * @dispatches settingStockItemEnableRules.getEnableStockItemRule
   * @dispatches settingStockItemEnableRules.getDisableStockItemRule
   * @dispatches settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrder
   * @dispatches settingStoreOverrides.getStoreOverride
   * @dispatches settingStoreStockEditThresholdRules.getStockEditThresholdRules
   * @dispatches settingStoreTagsSettings.getStoreTags
   * @dispatches settingSharedLineColours.getLineColours
   * @returns initSettings.complete when all settings have been loaded
   */
  start$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initSettings.start),
      exhaustMap((_) => {
        const requestId = generateHexId();

        //#region DISPATCHES
        this._store.dispatch(setUserAccessSettingsDefaults.setDefaultSettings({
          requestId,
        }));
        this._store.dispatch(settingUserDateFormatting.getDateFormatting({
          requestId,
        }));
        this._store.dispatch(settingUserStoreDisplayOrder.getStoreDisplayOrder({
          requestId,
        }));
        this._store.dispatch(settingStoreStockItemAddRule.getAddItemRules({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreAOSupplierEmails.getAutoOrderSupplierEmails({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreAOExpirationSettings.getAOExpiration({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStockItemEnableRules.getEnableStockItemRule({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStockItemEnableRules.getDisableStockItemRule({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrder({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreOverrides.getStoreOverride({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreProcessedOrder.getStoreProcessedOrder({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreStockEditThresholdRules.getStockEditThresholdRules({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStorePriceBands.getPriceBands({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRules({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingStoreTagsSettings.getStoreTags({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingSharedLineColours.getLineColours({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingGlobalTagsSettings.getGlobalTags({
          params: {storeAction: 'All'},
          requestId,
        }));
        this._store.dispatch(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations());
        //#endregion

        return forkJoin([
          this.waitForActionOutcome(
            settingUserDateFormatting.getDateFormattingSuccess,
            settingUserDateFormatting.getDateFormattingFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingUserStoreDisplayOrder.getStoreDisplayOrderSuccess,
            settingUserStoreDisplayOrder.getStoreDisplayOrderFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreStockItemAddRule.getAddItemRulesSuccess,
            settingStoreStockItemAddRule.getAddItemRulesFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsSuccess,
            settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreAOExpirationSettings.getAOExpirationSuccess,
            settingStoreAOExpirationSettings.getAOExpirationFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStockItemEnableRules.getEnableStockItemRuleSuccess,
            settingStockItemEnableRules.getEnableStockItemRuleFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStockItemEnableRules.getDisableStockItemRuleSuccess,
            settingStockItemEnableRules.getDisableStockItemRuleFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderSuccess,
            settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreOverrides.getStoreOverrideSuccess,
            settingStoreOverrides.getStoreOverrideFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreProcessedOrder.getStoreProcessedOrderSuccess,
            settingStoreProcessedOrder.getStoreProcessedOrderFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreStockEditThresholdRules.getStockEditThresholdRulesSuccess,
            settingStoreStockEditThresholdRules.getStockEditThresholdRulesFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStorePriceBands.getPriceBandsSuccess,
            settingStorePriceBands.getPriceBandsFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingStoreTagsSettings.getStoreTagsSuccess,
            settingStoreTagsSettings.getStoreTagsFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingSharedLineColours.getLineColoursSuccess,
            settingSharedLineColours.getLineColoursFailure,
            requestId,
          ),
          this.waitForActionOutcome(
            settingGlobalTagsSettings.getGlobalTagsSuccess,
            settingGlobalTagsSettings.getGlobalTagsFailure,
            requestId,
          ),
        ]).pipe(
          map(() => initSettings.complete()),
        );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Date Formatting

  /**
   * Retrieves user date formatting preferences from Firestore.
   * @effect getDateFormatting$
   * @listens settingUserDateFormatting.getDateFormatting$
   * @returns settingUserDateFormatting.getDateFormattingSuccess on success
   * @returns settingUserDateFormatting.getDateFormattingFailure on error
   */
  getDateFormatting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserDateFormatting.getDateFormatting),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).dateFormatting();
        return this.settingService.getDocumentAsObservable<{ dateSettings: TLocaleDateFormat }>(path)
          .pipe(
            map((response: {
              dateSettings: TLocaleDateFormat
            }) => settingUserDateFormatting.getDateFormattingSuccess({format: response.dateSettings, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserDateFormatting.getDateFormattingFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user date formatting preferences in Firestore.
   * @effect setDateFormatting$
   * @listens settingUserDateFormatting.setDateFormatting$
   * @returns settingUserDateFormatting.setDateFormattingSuccess on success
   * @returns settingUserDateFormatting.setDateFormattingFailure on error
   */
  setDateFormatting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserDateFormatting.setDateFormatting),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const path = createUserPaths().setUserId(user.id).dateFormatting();
        const {format} = action;
        return this.settingService.updateDocumentAsObservable<{
          dateSettings: string
        }>(path, {dateSettings: format}, true)
          .pipe(
            map(() => settingUserDateFormatting.setDateFormattingSuccess({format})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserDateFormatting.setDateFormattingFailure({error: globalError}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Store Display Order

  /**
   * Retrieves user store display order preferences from Firestore.
   * @effect getStoreDisplayOrder$
   * @listens settingUserStoreDisplayOrder.getStoreDisplayOrder$
   * @returns settingUserStoreDisplayOrder.getStoreDisplayOrderSuccess on success
   * @returns settingUserStoreDisplayOrder.getStoreDisplayOrderFailure on error
   */
  getStoreDisplayOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreDisplayOrder.getStoreDisplayOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).storeDisplayOrder();
        return this.settingService.getDocumentAsObservable<{
          displayOrder: TStoreDisplayOrder;
          customOrder?: string[];
        }>(path)
          .pipe(
            map((response: {
              displayOrder?: TStoreDisplayOrder;
              customOrder?: string[];
            }) => settingUserStoreDisplayOrder.getStoreDisplayOrderSuccess({
              order: response?.displayOrder ?? 'Alphabetical',
              customStoreOrder: response?.customOrder ?? [],
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreDisplayOrder.getStoreDisplayOrderFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user store display order preferences in Firestore.
   * @effect setStoreDisplayOrder$
   * @listens settingUserStoreDisplayOrder.setStoreDisplayOrder$
   * @returns settingUserStoreDisplayOrder.setStoreDisplayOrderSuccess on success
   * @returns settingUserStoreDisplayOrder.setStoreDisplayOrderFailure on error
   */
  setStoreDisplayOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreDisplayOrder.setStoreDisplayOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const path = createUserPaths().setUserId(user.id).storeDisplayOrder();
        const {order, customStoreOrder} = action;
        const updateData: {
          displayOrder: TStoreDisplayOrder;
          customOrder?: string[];
        } = {
          displayOrder: order,
          ...(customStoreOrder && {customOrder: customStoreOrder}),
        };
        return this.settingService.updateDocumentAsObservable(path, updateData, true)
          .pipe(
            map(() => settingUserStoreDisplayOrder.setStoreDisplayOrderSuccess({
              order,
              customStoreOrder,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreDisplayOrder.setStoreDisplayOrderFailure({error: globalError}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Fit To Grid

  /**
   * Retrieves user table fit-to-grid preferences from Firestore.
   * @effect getTableFitToGrid$
   * @listens settingUserFitToGrid.getTableFitToGrid$
   * @returns settingUserFitToGrid.getTableFitToGridSuccess on success
   * @returns settingUserFitToGrid.getTableFitToGridFailure on error
   */
  getTableFitToGrid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserFitToGrid.getFitToGrid),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).fitToGrid();
        return this.settingService.getDocumentAsObservable<{ value: boolean }>(path)
          .pipe(
            map((fit: { value: boolean }) => settingUserFitToGrid.getFitToGridSuccess({fit: fit.value, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserFitToGrid.getFitToGridFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user table fit-to-grid preferences in Firestore.
   * @effect setTableFitToGrid$
   * @listens settingUserFitToGrid.setTableFitToGrid$
   * @returns settingUserFitToGrid.setTableFitToGridSuccess on success
   * @returns settingUserFitToGrid.setTableFitToGridFailure on error
   */
  setTableFitToGrid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserFitToGrid.setFitToGrid),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {fit, requestId} = action;
        const path = createUserPaths().setUserId(user.id).fitToGrid();
        return this.settingService.updateDocumentAsObservable<{ value: boolean }>(path, {value: fit}, true)
          .pipe(
            map(() => settingUserFitToGrid.setFitToGridSuccess({fit, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserFitToGrid.setFitToGridFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Edit Preview Cols NGP Report

  /**
   * Retrieves user edit preview columns settings for NGP reports from Firestore.
   * @effect getEditPreviewColsNGPReport$
   * @listens settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReport$
   * @returns settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportSuccess on success
   * @returns settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportFailure on error
   */
  getEditPreviewColsNGPReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReport),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).ngpEditsPreview();
        return this.settingService.getDocumentAsObservable<{ [index: number]: string }>(path)
          .pipe(
            map((visibleFields: {
              [index: number]: string
            }) => settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportSuccess({visibleFields, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user edit preview columns settings for NGP reports in Firestore.
   * @effect setEditPreviewColsNGPReport$
   * @listens settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReport$
   * @returns settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportSuccess on success
   * @returns settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportFailure on error
   */
  setEditPreviewColsNGPReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReport),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {visibleFields, requestId} = action;
        const path = createUserPaths().setUserId(user.id).ngpEditsPreview();
        return this.settingService.updateDocumentAsObservable<{ [index: number]: string }>(path, visibleFields, true)
          .pipe(
            map(() => settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportSuccess({
              visibleFields,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Edit Preview Cols Stock

  /**
   * Retrieves user edit preview columns settings for stock items from Firestore.
   * @effect getEditPreviewColsStock$
   * @listens settingUserEditPreviewColsStock.getEditPreviewColsStock$
   * @returns settingUserEditPreviewColsStock.getEditPreviewColsStockSuccess on success
   * @returns settingUserEditPreviewColsStock.getEditPreviewColsStockFailure on error
   */
  getEditPreviewColsStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserEditPreviewColsStock.getEditPreviewColsStock),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).stockEditsPreview();
        return this.settingService.getDocumentAsObservable<{ [index: number]: string }>(path)
          .pipe(
            map((visibleFields: {
              [index: number]: string
            }) => settingUserEditPreviewColsStock.getEditPreviewColsStockSuccess({visibleFields, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserEditPreviewColsStock.getEditPreviewColsStockFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user edit preview columns settings for stock items in Firestore.
   * @effect setEditPreviewColsStock$
   * @listens settingUserEditPreviewColsStock.setEditPreviewColsStock$
   * @returns settingUserEditPreviewColsStock.setEditPreviewColsStockSuccess on success
   * @returns settingUserEditPreviewColsStock.setEditPreviewColsStockFailure on error
   */
  setEditPreviewColsStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserEditPreviewColsStock.setEditPreviewColsStock),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {visibleFields, requestId} = action;
        const path = createUserPaths().setUserId(user.id).stockEditsPreview();
        return this.settingService.updateDocumentAsObservable<{ [index: number]: string }>(path, visibleFields, true)
          .pipe(
            map(() => settingUserEditPreviewColsStock.setEditPreviewColsStockSuccess({visibleFields, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserEditPreviewColsStock.setEditPreviewColsStockFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User] Get / Set Table Preferences

  /**
   * Retrieves user table navigation preferences from Firestore.
   * @effect getTablePreferences$
   * @listens settingUserTablePreferences.getTablePreferences$
   * @returns settingUserTablePreferences.getTablePreferencesSuccess on success
   * @returns settingUserTablePreferences.getTablePreferencesFailure on error
   */
  getTablePreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserTablePreferences.getTablePreferences),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).tableNavigation();

        return this.settingService.getDocumentAsObservable<ITableNavSettings>(path)
          .pipe(
            map((settings: ITableNavSettings) => settingUserTablePreferences.getTablePreferencesSuccess({
              settings,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserTablePreferences.getTablePreferencesFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user table navigation preferences in Firestore.
   * @effect setTablePreferences$
   * @listens settingUserTablePreferences.setTablePreferences$
   * @returns settingUserTablePreferences.setTablePreferencesSuccess on success
   * @returns settingUserTablePreferences.setTablePreferencesFailure on error
   */
  setTablePreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserTablePreferences.setTablePreferences),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {settings, requestId} = action;
        const path = createUserPaths().setUserId(user.id).tableNavigation();
        return this.settingService.updateDocumentAsObservable(path, settings, true)
          .pipe(
            map(() => settingUserTablePreferences.setTablePreferencesSuccess({settings, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserTablePreferences.setTablePreferencesFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User Store] Get / Set Mailer Configuration

  /**
   * Retrieves user mailer configuration settings for the selected store from Firestore.
   * @effect getMailerConfiguration$
   * @listens settingUserStoreMailerConfiguration.getMailerConfiguration$
   * @returns settingUserStoreMailerConfiguration.getMailerConfigurationSuccess on success
   * @returns settingUserStoreMailerConfiguration.getMailerConfigurationFailure on error
   */
  getMailerConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.getMailerConfiguration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).mailerSettings(selectedStore.storeId);
        return this.settingService.getDocumentAsObservable<IEmailSettings>(path)
          .pipe(
            map((mailSettings: IEmailSettings) => settingUserStoreMailerConfiguration.getMailerConfigurationSuccess({
              mailSettings: sanitizeMailerSettings(mailSettings),
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreMailerConfiguration.getMailerConfigurationFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user mailer configuration settings for the selected store in Firestore.
   * @effect setMailerConfiguration$
   * @listens settingUserStoreMailerConfiguration.setMailerConfiguration$
   * @returns settingUserStoreMailerConfiguration.setMailerConfigurationSuccess on success
   * @returns settingUserStoreMailerConfiguration.setMailerConfigurationFailure on error
   */
  setMailerConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.setMailerConfiguration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {mailSettings, requestId} = action;
        const path = createUserPaths().setUserId(user.id).mailerSettings(selectedStore.storeId);
        return this.settingService.updateDocumentAsObservable<IEmailSettings>(path, mailSettings, true)
          .pipe(
            map(() => settingUserStoreMailerConfiguration.setMailerConfigurationSuccess({mailSettings, requestId})),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreMailerConfiguration.setMailerConfigurationFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | User Store] Get / Set Order Configuration

  /**
   * Retrieves user order configuration settings for the selected store from Firestore.
   * @effect getOrderConfiguration$
   * @listens settingUserStoreOrderConfiguration.getOrderConfiguration$
   * @returns settingUserStoreOrderConfiguration.getOrderConfigurationSuccess on success
   * @returns settingUserStoreOrderConfiguration.getOrderConfigurationFailure on error
   */
  getOrderConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreOrderConfiguration.getOrderConfiguration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {requestId} = action;
        const path = createUserPaths().setUserId(user.id).orderSettings(selectedStore.storeId);
        return this.settingService.getDocumentAsObservable<IAutoOrderOrderingSettings>(path)
          .pipe(
            map((orderSettings: IAutoOrderOrderingSettings) => settingUserStoreOrderConfiguration.getOrderConfigurationSuccess({
              orderSettings, store: selectedStore, requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreOrderConfiguration.getOrderConfigurationFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Updates user order configuration settings for the selected store in Firestore.
   * @effect setOrderConfiguration$
   * @listens settingUserStoreOrderConfiguration.setOrderConfiguration$
   * @returns settingUserStoreOrderConfiguration.setOrderConfigurationSuccess on success
   * @returns settingUserStoreOrderConfiguration.setOrderConfigurationFailure on error
   */
  setOrderConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreOrderConfiguration.setOrderConfiguration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {orderSettings, requestId} = action;
        const path = createUserPaths().setUserId(user.id).orderSettings(selectedStore.storeId);
        return this.settingService.updateDocumentAsObservable<IAutoOrderOrderingSettings>(path, orderSettings, true)
          .pipe(
            map(() => settingUserStoreOrderConfiguration.setOrderConfigurationSuccess({
              orderSettings, store: selectedStore, requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingUserStoreOrderConfiguration.setOrderConfigurationFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Stock Item Add Rules

  /**
   * Retrieves stock item add rules from Firestore for selected or all stores.
   * @effect getAddItemRules$
   * @listens settingStoreStockItemAddRule.getAddItemRules$
   * @returns settingStoreStockItemAddRule.getAddItemRulesSuccess on success
   * @returns settingStoreStockItemAddRule.getAddItemRulesFailure on error
   */
  getAddItemRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreStockItemAddRule.getAddItemRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAddItemRules();
          return this.settingService.getDocumentAsObservable<IAddItemRule>(path).pipe(
            take(1),
            map((rules) =>
              settingStoreStockItemAddRule.getAddItemRulesSuccess({
                params: {
                  settings: {[selectedStore.storeId]: rules},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(settingStoreStockItemAddRule.getAddItemRulesFailure({error: globalError, requestId}));
            }),
          );
        }

        const CONCURRENCY = settingsStores.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsAddItemRules();
            return this.settingService.getDocumentAsObservable<IAddItemRule>(path).pipe(
              take(1),
              map((rules) => ({ok: true as const, store, rules})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IAddItemRule> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.rules;
              } else if (!r.ok) {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreStockItemAddRule.getAddItemRulesSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(settingStoreStockItemAddRule.getAddItemRulesFailure({error: globalError, requestId}));
          }),
        );
      }),
    ),
  );

  /**
   * Updates stock item add rules in Firestore for the selected store.
   * @effect setAddItemRules$
   * @listens settingStoreStockItemAddRule.setAddItemRules$
   * @returns settingStoreStockItemAddRule.setAddItemRulesSuccess on success
   * @returns settingStoreStockItemAddRule.setAddItemRulesFailure on error
   */
  setAddItemRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreStockItemAddRule.setAddItemRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {rules, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAddItemRules();
        return this.settingService.updateDocumentAsObservable(path, rules, true)
          .pipe(
            map(() => settingStoreStockItemAddRule.setAddItemRulesSuccess({
              store: selectedStore,
              rules,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreStockItemAddRule.setAddItemRulesFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set AO Supplier Emails

  /**
   * Retrieves auto-order supplier email settings from Firestore for selected or all stores.
   * @effect getAutoOrderSupplierEmails$
   * @listens settingStoreAOSupplierEmails.getAutoOrderSupplierEmails$
   * @returns settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsSuccess on success
   * @returns settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsFailure on error
   */
  getAutoOrderSupplierEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOSupplierEmails.getAutoOrderSupplierEmails),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderSupplierEmails();
          return this.settingService.getDocumentAsObservable<{ [account: string]: TSupplierEmail[] }>(path).pipe(
            take(1),
            map((emails) =>
              settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsSuccess({
                params: {
                  settings: {[selectedStore.storeId]: emails},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsAutoOrderSupplierEmails();
            return this.settingService.getDocumentAsObservable<{ [account: string]: TSupplierEmail[] }>(path).pipe(
              take(1),
              map((emails) => ({ok: true as const, store, emails})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, { [account: string]: TSupplierEmail[] }> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.emails;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates auto-order supplier email settings in Firestore for the selected store.
   * @effect setAutoOrderSupplierEmails$
   * @listens settingStoreAOSupplierEmails.setAutoOrderSupplierEmails$
   * @returns settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsSuccess on success
   * @returns settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsFailure on error
   */
  setAutoOrderSupplierEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOSupplierEmails.setAutoOrderSupplierEmails),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {emails, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderSupplierEmails();
        return this.settingService.updateDocumentAsObservable<{
          [account: string]: TSupplierEmail[]
        }>(path, emails, true)
          .pipe(
            map(() => settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsSuccess({
                emails,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set AO Expiration

  /**
   * Retrieves auto-order expiration settings from Firestore for selected or all stores.
   * @effect getAOExpiration$
   * @listens settingStoreAOExpirationSettings.getAOExpiration$
   * @returns settingStoreAOExpirationSettings.getAOExpirationSuccess on success
   * @returns settingStoreAOExpirationSettings.getAOExpirationFailure on error
   */
  getAOExpiration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOExpirationSettings.getAOExpiration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderExpiration();
          return this.settingService.getDocumentAsObservable<IAutoOrderExpirationSettings>(path).pipe(
            take(1),
            map((setting) =>
              settingStoreAOExpirationSettings.getAOExpirationSuccess({
                params: {
                  settings: {[selectedStore.storeId]: setting},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(settingStoreAOExpirationSettings.getAOExpirationFailure({error: globalError, requestId}));
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsAutoOrderExpiration();
            return this.settingService.getDocumentAsObservable<IAutoOrderExpirationSettings>(path).pipe(
              take(1),
              map((setting) => ({ok: true as const, store, setting})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IAutoOrderExpirationSettings> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.setting;
              } else if (!r.ok) {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreAOExpirationSettings.getAOExpirationSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(settingStoreAOExpirationSettings.getAOExpirationFailure({error: globalError, requestId}));
          }),
        );
      }),
    ),
  );

  /**
   * Updates auto-order expiration settings in Firestore for the selected store.
   * @effect setAOExpiration$
   * @listens settingStoreAOExpirationSettings.setAOExpiration$
   * @returns settingStoreAOExpirationSettings.setAOExpirationSuccess on success
   * @returns settingStoreAOExpirationSettings.setAOExpirationFailure on error
   */
  setAOExpiration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOExpirationSettings.setAOExpiration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {settings, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderExpiration();
        return this.settingService.updateDocumentAsObservable<IAutoOrderExpirationSettings>(path, settings, true)
          .pipe(
            map(() => settingStoreAOExpirationSettings.setAOExpirationSuccess({
                settings,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOExpirationSettings.setAOExpirationFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Stock Item Enable and Disable Rules

  /**
   * Retrieves stock item enable rules from Firestore for selected or all stores.
   * @effect getEnableStockItemRule$
   * @listens settingStockItemEnableRules.getEnableStockItemRule$
   * @returns settingStockItemEnableRules.getEnableStockItemRuleSuccess on success
   * @returns settingStockItemEnableRules.getEnableStockItemRuleFailure on error
   */
  getEnableStockItemRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStockItemEnableRules.getEnableStockItemRule),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsEnableStockItemRules();
          return this.settingService.getDocumentAsObservable<IEnableStockItemRule>(path).pipe(
            take(1),
            map((rules) =>
              settingStockItemEnableRules.getEnableStockItemRuleSuccess({
                params: {
                  settings: {[selectedStore.storeId]: rules},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStockItemEnableRules.getEnableStockItemRuleFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsEnableStockItemRules();
            return this.settingService.getDocumentAsObservable<IEnableStockItemRule>(path).pipe(
              take(1),
              map((rules) => ({ok: true as const, store, rules})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IEnableStockItemRule> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.rules;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStockItemEnableRules.getEnableStockItemRuleSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStockItemEnableRules.getEnableStockItemRuleFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates stock item enable rules in Firestore for the selected store.
   * @effect setEnableStockItemRule$
   * @listens settingStockItemEnableRules.setEnableStockItemRule$
   * @returns settingStockItemEnableRules.setEnableStockItemRuleSuccess on success
   * @returns settingStockItemEnableRules.setEnableStockItemRuleFailure on error
   */
  setEnableStockItemRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStockItemEnableRules.setEnableStockItemRule),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {settings, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsEnableStockItemRules();
        return this.settingService.updateDocumentAsObservable<IEnableStockItemRule>(path, settings, true)
          .pipe(
            map(() => settingStockItemEnableRules.setEnableStockItemRuleSuccess({
                settings,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStockItemEnableRules.setEnableStockItemRuleFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Retrieves stock item disable rules from Firestore for selected or all stores.
   * @effect getDisableStockItemRule$
   * @listens settingStockItemEnableRules.getDisableStockItemRule$
   * @returns settingStockItemEnableRules.getDisableStockItemRuleSuccess on success
   * @returns settingStockItemEnableRules.getDisableStockItemRuleFailure on error
   */
  getDisableStockItemRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStockItemEnableRules.getDisableStockItemRule),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsDisableStockItemRules();
          return this.settingService.getDocumentAsObservable<IDisableStockItemRule>(path).pipe(
            take(1),
            map((rules) =>
              settingStockItemEnableRules.getDisableStockItemRuleSuccess({
                params: {
                  settings: {[selectedStore.storeId]: rules},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStockItemEnableRules.getDisableStockItemRuleFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsDisableStockItemRules();
            return this.settingService.getDocumentAsObservable<IDisableStockItemRule>(path).pipe(
              take(1),
              map((rules) => ({ok: true as const, store, rules})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IDisableStockItemRule> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.rules;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStockItemEnableRules.getDisableStockItemRuleSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStockItemEnableRules.getDisableStockItemRuleFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates stock item disable rules in Firestore for the selected store.
   * Automatically syncs prefixes and suffixes to enable rules to allow re-enabling.
   * @effect setDisableStockItemRule$
   * @listens settingStockItemEnableRules.setDisableStockItemRule$
   * @returns settingStockItemEnableRules.setDisableStockItemRuleSuccess on success
   * @returns settingStockItemEnableRules.setDisableStockItemRuleFailure on error
   */
  setDisableStockItemRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStockItemEnableRules.setDisableStockItemRule),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(SettingSelectors.selectSelectedStoreEnableItemRule)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, currentEnableRules]) => {
        const {settings, requestId} = action;
        const disablePath = createStorePaths().setStoreId(selectedStore.storeId).settingsDisableStockItemRules();
        const firestoreSettings = sanitizeDisableStockItemRuleForFirestore(settings);

        return this.settingService.updateDocumentAsObservable<IDisableStockItemRule>(disablePath, firestoreSettings as IDisableStockItemRule, true)
          .pipe(
            switchMap(() => {
              // Auto-sync: Add disable rule prefixes/suffixes to enable rules arrays
              const enableRulesDocCopy: IEnableStockItemRule = structuredClone(currentEnableRules);

              // Extract and trim prefixes/suffixes from disable rules
              const validZeroPrefix = settings.onHandZero?.descPrefix?.trim() || '';
              const validNotZeroPrefix = settings.onHandNotZero?.descPrefix?.trim() || '';
              const validZeroSuffix = settings.onHandZero?.descSuffix?.trim() || '';
              const validNotZeroSuffix = settings.onHandNotZero?.descSuffix?.trim() || '';

              // Filter out empty strings
              const prefixesToAdd = [validZeroPrefix, validNotZeroPrefix].filter((prefix: string) => prefix !== '');
              const suffixesToAdd = [validZeroSuffix, validNotZeroSuffix].filter((suffix: string) => suffix !== '');

              // Merge with existing arrays using Set for uniqueness
              enableRulesDocCopy.descPrefixArray = Array.from(new Set([
                ...(currentEnableRules.descPrefixArray || []),
                ...prefixesToAdd,
              ]));

              enableRulesDocCopy.descSuffixArray = Array.from(new Set([
                ...(currentEnableRules.descSuffixArray || []),
                ...suffixesToAdd,
              ]));

              // Update enable rules in Firestore
              const enablePath = createStorePaths().setStoreId(selectedStore.storeId).settingsEnableStockItemRules();
              return this.settingService.updateDocumentAsObservable<IEnableStockItemRule>(enablePath, enableRulesDocCopy, true)
                .pipe(
                  map(() => settingStockItemEnableRules.setDisableStockItemRuleSuccess({
                      settings,
                      store: selectedStore,
                      requestId,
                    }),
                  ),
                );
            }),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStockItemEnableRules.setDisableStockItemRuleFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set AO Full Open Order

  /**
   * Retrieves auto-order open order settings from Firestore for selected or all stores.
   * @effect getAOFullOpenOrder$
   * @listens settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrder$
   * @returns settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderSuccess on success
   * @returns settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderFailure on error
   */
  getAOFullOpenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOpenOrderSettings();
          return this.settingService.getDocumentAsObservable<IOpenOrderSettings>(path).pipe(
            take(1),
            map((rules) =>
              settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderSuccess({
                params: {
                  settings: {[selectedStore.storeId]: rules},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsOpenOrderSettings();
            return this.settingService.getDocumentAsObservable<IOpenOrderSettings>(path).pipe(
              take(1),
              map((rules) => ({ok: true as const, store, rules})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IOpenOrderSettings> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.rules;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates auto-order open order settings in Firestore for the selected store.
   * @effect setAOFullOpenOrder$
   * @listens settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrder$
   * @returns settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderSuccess on success
   * @returns settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderFailure on error
   */
  setAOFullOpenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {settings, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOpenOrderSettings();
        return this.settingService.updateDocumentAsObservable<IOpenOrderSettings>(path, settings, true)
          .pipe(
            map(() => settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderSuccess({
                settings,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set AO Order Scheduler (Account Settings)

  /**
   * Retrieves auto-order scheduler (account settings) from Firestore for selected or all stores.
   * Reads the open order settings document and extracts the orderAccountSettings field.
   * @effect getAOOrderScheduler$
   * @listens settingStoreAOStoreOrderScheduler.getAOOrderScheduler$
   * @returns settingStoreAOStoreOrderScheduler.getAOOrderSchedulerSuccess on success
   * @returns settingStoreAOStoreOrderScheduler.getAOOrderSchedulerFailure on error
   */
  getAOOrderScheduler$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOStoreOrderScheduler.getAOOrderScheduler),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOpenOrderSettings();
          return this.settingService.getDocumentAsObservable<IOpenOrderSettings>(path).pipe(
            take(1),
            map((openOrderSettings) =>
              settingStoreAOStoreOrderScheduler.getAOOrderSchedulerSuccess({
                params: {
                  settings: {[selectedStore.storeId]: openOrderSettings?.orderAccountSettings ?? _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOStoreOrderScheduler.getAOOrderSchedulerFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsOpenOrderSettings();
            return this.settingService.getDocumentAsObservable<IOpenOrderSettings>(path).pipe(
              take(1),
              map((openOrderSettings) => ({
                ok: true as const,
                store,
                settings: openOrderSettings?.orderAccountSettings ?? _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS,
              })),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IAutoOrderAccountSettings> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.settings;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreAOStoreOrderScheduler.getAOOrderSchedulerSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreAOStoreOrderScheduler.getAOOrderSchedulerFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates auto-order scheduler (account settings) in Firestore for the selected store.
   * @effect setAOOrderScheduler$
   * @listens settingStoreAOStoreOrderScheduler.setAOOrderScheduler$
   * @returns settingStoreAOStoreOrderScheduler.setAOOrderSchedulerSuccess on success
   * @returns settingStoreAOStoreOrderScheduler.setAOOrderSchedulerFailure on error
   */
  setAOOrderScheduler$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreAOStoreOrderScheduler.setAOOrderScheduler),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {settings, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOpenOrderSettings();
        const update: Partial<IOpenOrderSettings> = {orderAccountSettings: settings};
        return this.settingService.updateDocumentAsObservable<Partial<IOpenOrderSettings>>(path, update, true)
          .pipe(
            map(() => settingStoreAOStoreOrderScheduler.setAOOrderSchedulerSuccess({
                settings,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreAOStoreOrderScheduler.setAOOrderSchedulerFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Store Overrides

  /**
   * Retrieves store override settings from Firestore for selected or all stores.
   * @effect getStoreOverride$
   * @listens settingStoreOverrides.getStoreOverride$
   * @returns settingStoreOverrides.getStoreOverrideSuccess on success
   * @returns settingStoreOverrides.getStoreOverrideFailure on error
   */
  getStoreOverride$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreOverrides.getStoreOverride),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOverrides();
          return this.settingService.getDocumentAsObservable<IStoreOverrideSettings>(path).pipe(
            take(1),
            map((overrides) =>
              settingStoreOverrides.getStoreOverrideSuccess({
                params: {
                  settings: {[selectedStore.storeId]: overrides},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreOverrides.getStoreOverrideFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsOverrides();
            return this.settingService.getDocumentAsObservable<IStoreOverrideSettings>(path).pipe(
              take(1),
              map((overrides) => ({ok: true as const, store, overrides})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IStoreOverrideSettings> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.overrides;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreOverrides.getStoreOverrideSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreOverrides.getStoreOverrideFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates store override settings in Firestore for the selected store.
   * @effect setStoreOverride$
   * @listens settingStoreOverrides.setStoreOverride$
   * @returns settingStoreOverrides.setStoreOverrideSuccess on success
   * @returns settingStoreOverrides.setStoreOverrideFailure on error
   */
  setStoreOverride$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreOverrides.setStoreOverride),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {overrides, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsOverrides();
        return this.settingService.updateDocumentAsObservable<IStoreOverrideSettings>(path, overrides, true)
          .pipe(
            map(() => settingStoreOverrides.setStoreOverrideSuccess({
              overrides,
              store: selectedStore,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreOverrides.setStoreOverrideFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Auto Order Processed Order Settings

  /**
   * Retrieves auto-order processed-order settings from Firestore for selected or all stores.
   * @effect getStoreProcessedOrder$
   * @listens settingStoreProcessedOrder.getStoreProcessedOrder$
   * @returns settingStoreProcessedOrder.getStoreProcessedOrderSuccess on success
   * @returns settingStoreProcessedOrder.getStoreProcessedOrderFailure on error
   */
  getStoreProcessedOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreProcessedOrder.getStoreProcessedOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderProcessed();
          return this.settingService.getDocumentAsObservable<IProcessedOrderSettings>(path).pipe(
            take(1),
            map((processedOrder) =>
              settingStoreProcessedOrder.getStoreProcessedOrderSuccess({
                params: {
                  settings: {[selectedStore.storeId]: processedOrder},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreProcessedOrder.getStoreProcessedOrderFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsAutoOrderProcessed();
            return this.settingService.getDocumentAsObservable<IProcessedOrderSettings>(path).pipe(
              take(1),
              map((processedOrder) => ({ok: true as const, store, processedOrder})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IProcessedOrderSettings> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.processedOrder;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreProcessedOrder.getStoreProcessedOrderSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreProcessedOrder.getStoreProcessedOrderFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates auto-order processed-order settings in Firestore for the selected store.
   * @effect setStoreProcessedOrder$
   * @listens settingStoreProcessedOrder.setStoreProcessedOrder$
   * @returns settingStoreProcessedOrder.setStoreProcessedOrderSuccess on success
   * @returns settingStoreProcessedOrder.setStoreProcessedOrderFailure on error
   */
  setStoreProcessedOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreProcessedOrder.setStoreProcessedOrder),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {processedOrder, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsAutoOrderProcessed();
        return this.settingService.updateDocumentAsObservable<IProcessedOrderSettings>(path, processedOrder, true)
          .pipe(
            map(() => settingStoreProcessedOrder.setStoreProcessedOrderSuccess({
              processedOrder,
              store: selectedStore,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreProcessedOrder.setStoreProcessedOrderFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Stock Edit Threshold Rules

  /**
   * Retrieves stock edit threshold rules from Firestore for selected or all stores.
   * @effect getStockEditThresholdRules$
   * @listens settingStoreStockEditThresholdRules.getStockEditThresholdRules$
   * @returns settingStoreStockEditThresholdRules.getStockEditThresholdRulesSuccess on success
   * @returns settingStoreStockEditThresholdRules.getStockEditThresholdRulesFailure on error
   */
  getStockEditThresholdRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreStockEditThresholdRules.getStockEditThresholdRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths().setStoreId(selectedStore.storeId).settingsStockEditThresholds();
          return this.settingService.getDocumentAsObservable<IStockItemEditThresholds>(path).pipe(
            take(1),
            map((rules) =>
              settingStoreStockEditThresholdRules.getStockEditThresholdRulesSuccess({
                params: {
                  settings: {[selectedStore.storeId]: rules},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreStockEditThresholdRules.getStockEditThresholdRulesFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsStockEditThresholds();
            return this.settingService.getDocumentAsObservable<IStockItemEditThresholds>(path).pipe(
              take(1),
              map((rules) => ({ok: true as const, store, rules})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, IStockItemEditThresholds> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.rules;
              } else {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreStockEditThresholdRules.getStockEditThresholdRulesSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreStockEditThresholdRules.getStockEditThresholdRulesFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates stock edit threshold rules in Firestore for the selected store.
   * @effect setStockEditThresholdRules$
   * @listens settingStoreStockEditThresholdRules.setStockEditThresholdRules$
   * @returns settingStoreStockEditThresholdRules.setStockEditThresholdRulesSuccess on success
   * @returns settingStoreStockEditThresholdRules.setStockEditThresholdRulesFailure on error
   */
  setStockEditThresholdRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreStockEditThresholdRules.setStockEditThresholdRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {rules, requestId} = action;
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsStockEditThresholds();
        return this.settingService.updateDocumentAsObservable<IStockItemEditThresholds>(path, rules, true)
          .pipe(
            map(() => settingStoreStockEditThresholdRules.setStockEditThresholdRulesSuccess({
              rules,
              store: selectedStore,
              requestId,
            })),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreStockEditThresholdRules.setStockEditThresholdRulesFailure({error: globalError, requestId}),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Price Bands

  /**
   * Retrieves store price bands and the global fallback for selected or all stores.
   */
  getPriceBands$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStorePriceBands.getPriceBands),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      mergeMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';
        const fetchStartedAt: number = Date.now();
        const globalPath = createGlobalPaths().priceBands();
        const storesToLoad: IStore[] = storeAction === 'Selected'
          ? (selectedStore ? [selectedStore] : [])
          : [...(settingsStores ?? [])];
        if (selectedStore?.storeId && !storesToLoad.some((store: IStore) => store.storeId === selectedStore.storeId)) {
          storesToLoad.unshift(selectedStore);
        }

        const globalBands$ = this.settingService.getDocumentAsObservable<Record<number, IPriceBand>>(globalPath).pipe(
          take(1),
          map((data: Record<number, IPriceBand> | undefined): IPriceBand[] => normalizePriceBandsDocument(data)),
          catchError(() => of([] as IPriceBand[])),
        );

        const storeBands$ = from(storesToLoad).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsPriceBands();
            return this.settingService.getDocumentAsObservable<Record<number, IPriceBand>>(path).pipe(
              take(1),
              map((data: Record<number, IPriceBand> | undefined) => ({
                ok: true as const,
                store,
                data,
              })),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, storesToLoad.length || 1),
          toArray(),
        );

        return forkJoin({
          globalBands: globalBands$,
          storeResults: storeBands$,
        }).pipe(
          map(({globalBands, storeResults}) => {
            const aggregated: Record<string, IPriceBandingStoreSettings> = {};
            const errors: IGlobalError[] = [];

            for (const result of storeResults) {
              if (!result.ok) {
                const err: IGlobalError = generateGlobalError(
                  JSON.stringify(action),
                  action.type,
                  result['error'],
                  user,
                  [result.store.storeId],
                );
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
                continue;
              }

              const storeBands: IPriceBand[] = normalizePriceBandsDocument(result.data);
              const hasStoreDocument: boolean = result.data !== undefined && result.data !== null;

              if (hasStoreDocument) {
                aggregated[result.store.storeId] = {
                  bands: storeBands,
                  source: 'store',
                };
                continue;
              }

              if (globalBands.length) {
                const persistPath: string = createStorePaths().setStoreId(result.store.storeId).settingsPriceBands();
                this.settingService.setDocumentOverwriteAsObservable(
                  persistPath,
                  toPriceBandsDocument(globalBands),
                ).subscribe({
                  error: (persistError: unknown) => {
                    if (logErrors) {
                      logConsoleError(action.type, {details: persistError});
                    }
                  },
                });
                aggregated[result.store.storeId] = {
                  bands: globalBands,
                  source: 'store',
                };
                continue;
              }

              aggregated[result.store.storeId] = {
                bands: globalBands,
                source: 'global',
              };
            }

            return settingStorePriceBands.getPriceBandsSuccess({
              errors,
              fetchStartedAt,
              globalBands,
              params: {
                settings: aggregated,
                storeAction,
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              storesToLoad.map((store: IStore) => store.storeId),
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(settingStorePriceBands.getPriceBandsFailure({error: globalError, requestId}));
          }),
        );
      }),
    ),
  );

  /**
   * Overwrites the selected store's price-band document.
   */
  setPriceBands$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStorePriceBands.setPriceBands),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {bands, requestId} = action;
        if (!selectedStore?.storeId) {
          const globalError: IGlobalError = generateGlobalError(
            JSON.stringify(action),
            action.type,
            new Error('No store is selected'),
            user,
            [],
          );
          return from([
            settingStorePriceBands.setPriceBandsFailure({error: globalError, requestId}),
            setGlobalErrorOccurrence({error: globalError}),
          ]);
        }
        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsPriceBands();
        return this.settingService.setDocumentOverwriteAsObservable(path, toPriceBandsDocument(bands)).pipe(
          map(() => settingStorePriceBands.setPriceBandsSuccess({
            bands,
            requestId,
            savedAt: Date.now(),
            source: 'store',
            store: selectedStore,
          })),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              [selectedStore.storeId],
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return from([
              settingStorePriceBands.setPriceBandsFailure({error: globalError, requestId}),
              setGlobalErrorOccurrence({error: globalError}),
            ]);
          }),
        );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Multiple Price Change Rules

  /**
   * Retrieves the multiple price change rules for the selected store or every store.
   */
  getMultiplePriceChangeRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      mergeMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';
        const storesToLoad: IStore[] = storeAction === 'Selected'
          ? (selectedStore ? [selectedStore] : [])
          : [...(settingsStores ?? [])];

        if (storesToLoad.length === 0) {
          return of(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRulesSuccess({
            params: {settings: {}, storeAction},
            requestId,
          }));
        }

        return from(storesToLoad).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths().setStoreId(store.storeId).settingsMultiplePriceChangeRules();
            return this.settingService.getDocumentAsObservable<IMultiplePriceChangeRulesDocument>(path).pipe(
              take(1),
              map((data: IMultiplePriceChangeRulesDocument | undefined) => ({ok: true as const, store, data})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, storesToLoad.length),
          toArray(),
          map((storeResults) => {
            const aggregated: Record<string, IMultiplePriceChangeRulesStoreSettings> = {};
            const errors: IGlobalError[] = [];

            for (const result of storeResults) {
              if (!result.ok) {
                const err: IGlobalError = generateGlobalError(
                  JSON.stringify(action),
                  action.type,
                  result['error'],
                  user,
                  [result.store.storeId],
                );
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
                continue;
              }

              aggregated[result.store.storeId] = normalizeMultiplePriceChangeRulesDocument(result.data);
            }

            return settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRulesSuccess({
              errors,
              params: {settings: aggregated, storeAction},
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              storesToLoad.map((store: IStore) => store.storeId),
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRulesFailure({error: globalError, requestId}));
          }),
        );
      }),
    ),
  );

  /**
   * Overwrites the selected store's multiple price change rules document.
   */
  setMultiplePriceChangeRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRules),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {settings, requestId} = action;
        if (!selectedStore?.storeId) {
          const globalError: IGlobalError = generateGlobalError(
            JSON.stringify(action),
            action.type,
            new Error('No store is selected'),
            user,
            [],
          );
          return from([
            settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRulesFailure({error: globalError, requestId}),
            setGlobalErrorOccurrence({error: globalError}),
          ]);
        }

        const path = createStorePaths().setStoreId(selectedStore.storeId).settingsMultiplePriceChangeRules();
        const document: IMultiplePriceChangeRulesDocument = toMultiplePriceChangeRulesDocument(settings);

        return this.settingService.setDocumentOverwriteAsObservable(path, document).pipe(
          map(() => settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRulesSuccess({
            requestId,
            savedAt: Date.now(),
            settings: {...settings, source: 'store'},
            store: selectedStore,
          })),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              [selectedStore.storeId],
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return from([
              settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRulesFailure({error: globalError, requestId}),
              setGlobalErrorOccurrence({error: globalError}),
            ]);
          }),
        );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Copy Store Settings

  /**
   * Copies the requested settings from the source store to the target store.
   * Price banding reads the source store document and falls back to the global
   * defaults when the source store has none, so the target always receives the
   * bands the source store is actually using.
   */
  copyStoreSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreCopySettings.copyStoreSettings),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      mergeMap(([action, user, logErrors]) => {
        const {requestId, settings, sourceStore, targetStore} = action;

        const copyPriceBands$: Observable<IPriceBand[] | null> = settings.includes('priceBanding')
          ? this.copyPriceBandsBetweenStores(sourceStore, targetStore)
          : of(null);

        return copyPriceBands$.pipe(
          map((priceBands: IPriceBand[] | null) => settingStoreCopySettings.copyStoreSettingsSuccess({
            priceBands: priceBands ?? undefined,
            requestId,
            savedAt: Date.now(),
            settings,
            sourceStore,
            targetStore,
          })),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              [sourceStore?.storeId, targetStore?.storeId].filter((storeId: string): boolean => !!storeId),
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return from([
              settingStoreCopySettings.copyStoreSettingsFailure({
                error: globalError,
                requestId,
                settings,
                targetStore,
              }),
              setGlobalErrorOccurrence({error: globalError}),
            ]);
          }),
        );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Store] Get / Set Store Tags

  /**
   * Retrieves store tags from Firestore for selected or all stores.
   * @effect getStoreTags$
   * @listens settingStoreTagsSettings.getStoreTags$
   * @returns settingStoreTagsSettings.getStoreTagsSuccess on success
   * @returns settingStoreTagsSettings.getStoreTagsFailure on error
   */
  getStoreTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreTagsSettings.getStoreTags),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createStorePaths()
            .setStoreId(selectedStore.storeId)
            .settingsStoreTags();

          return this.settingService
            .getDocumentAsObservable<{ tags: TStockTag[] }>(path)
            .pipe(
              take(1),
              map((data: { tags: TStockTag[] }) =>
                settingStoreTagsSettings.getStoreTagsSuccess({
                  params: {
                    settings: {
                      [selectedStore.storeId]: data.tags || [],
                    },
                    storeAction: 'Selected',
                  },
                  requestId,
                }),
              ),
              catchError((error) => {
                const globalError: IGlobalError = generateGlobalError(
                  JSON.stringify(action),
                  action.type,
                  error,
                  user,
                  [selectedStore.storeId],
                );
                if (logErrors) {
                  logConsoleError(action.type, {details: globalError});
                }
                return of(
                  settingStoreTagsSettings.getStoreTagsFailure({
                    error: globalError,
                    requestId,
                  }),
                );
              }),
            );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createStorePaths()
              .setStoreId(store.storeId)
              .settingsStoreTags();

            return this.settingService
              .getDocumentAsObservable<{ tags: TStockTag[] }>(path)
              .pipe(
                take(1),
                map((data) => ({ok: true as const, store, data})),
                catchError((error) => of({ok: false as const, store, error})),
              );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, TStockTag[]> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok && r.data?.tags?.length) {
                aggregated[r.store.storeId] = r.data.tags || [];
              } else if (!r.ok) {
                const err: IGlobalError = generateGlobalError(
                  JSON.stringify(action),
                  action.type,
                  r['error'],
                  user,
                  [r.store.storeId],
                );
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingStoreTagsSettings.getStoreTagsSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(
              JSON.stringify(action),
              action.type,
              error,
              user,
              (settingsStores ?? []).map((s: IStore) => s.storeId),
            );
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingStoreTagsSettings.getStoreTagsFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Updates store tags in Firestore for the selected store.
   * @effect setStoreTags$
   * @listens settingStoreTagsSettings.setStoreTags$
   * @returns settingStoreTagsSettings.setStoreTagsSuccess on success
   * @returns settingStoreTagsSettings.setStoreTagsFailure on error
   */
  setStoreTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingStoreTagsSettings.setStoreTags),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const {tags, requestId} = action; // tags: IGlobalStockTag[] (store tags only)
        const path = createStorePaths()
          .setStoreId(selectedStore.storeId)
          .settingsStoreTags();

        const firestoreTags: TStockTag[] = tags.map(
          (t: IGlobalStockTag) => t.name,
        );

        const data: { tags: TStockTag[] } = {tags: firestoreTags};

        return this.settingService
          .updateDocumentAsObservable<{ tags: TStockTag[] }>(path, data, true)
          .pipe(
            map(() =>
              settingStoreTagsSettings.setStoreTagsSuccess({
                tags: firestoreTags,
                store: selectedStore,
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(
                JSON.stringify(action),
                action.type,
                error,
                user,
                [selectedStore.storeId],
              );
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingStoreTagsSettings.setStoreTagsFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
      }),
    ),
  );

  //#endregion

  //#region [Settings | Shared] Get Line Colours

  /**
   * Retrieves line colours from Firestore for selected or all stores.
   * @effect getLineColours$
   * @listens settingSharedLineColours.getLineColours$
   * @returns settingSharedLineColours.getLineColoursSuccess on success
   * @returns settingSharedLineColours.getLineColoursFailure on error
   */
  getLineColours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingSharedLineColours.getLineColours),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
        this._store.pipe(select(StoreSelectors.selectStores)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore, settingsStores]) => {
        const {params, requestId} = action;
        const storeAction = params?.storeAction ?? 'Selected';

        if (storeAction === 'Selected') {
          const path = createSharedPaths().setStoreId(selectedStore.storeId).lineColours();
          return this.settingService.getDocumentAsObservable<TLineColour>(path).pipe(
            take(1),
            map((data) =>
              settingSharedLineColours.getLineColoursSuccess({
                params: {
                  settings: {[selectedStore.storeId]: data},
                  storeAction: 'Selected',
                },
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingSharedLineColours.getLineColoursFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
        }

        const CONCURRENCY = settingsStores?.length ?? 5;

        return from(settingsStores ?? []).pipe(
          mergeMap((store: IStore) => {
            const path = createSharedPaths().setStoreId(store.storeId).lineColours();
            return this.settingService.getDocumentAsObservable<TLineColour>(path).pipe(
              take(1),
              map((data) => ({ok: true as const, store, data})),
              catchError((error) => of({ok: false as const, store, error})),
            );
          }, CONCURRENCY),
          toArray(),
          map((results) => {
            const aggregated: Record<string, TLineColour> = {};
            const errors: IGlobalError[] = [];

            for (const r of results) {
              if (r.ok) {
                aggregated[r.store.storeId] = r.data;
              } else if (!r.ok) {
                const err: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, r['error'], user, [r.store.storeId]);
                errors.push(err);
                if (logErrors) {
                  logConsoleError(action.type, {details: err});
                }
              }
            }

            return settingSharedLineColours.getLineColoursSuccess({
              errors,
              params: {
                settings: aggregated,
                storeAction: 'All',
              },
              requestId,
            });
          }),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, (settingsStores ?? []).map((s: IStore) => s.storeId));
            if (logErrors) {
              logConsoleError(action.type, {details: globalError});
            }
            return of(
              settingSharedLineColours.getLineColoursFailure({
                error: globalError,
                requestId,
              }),
            );
          }),
        );
      }),
    ),
  );
  //#endregion

  //#region [Settings | Global] Get Global Tags

  /**
   * Retrieves global tags from Firestore.
   * @effect getGlobalTags$
   * @listens settingGlobalTagsSettings.getGlobalTags$
   * @returns settingGlobalTagsSettings.getGlobalTagsSuccess on success
   * @returns settingGlobalTagsSettings.getGlobalTagsFailure on error
   */
  getGlobalTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingGlobalTagsSettings.getGlobalTags),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const {requestId} = action;
        const globalPath = createGlobalPaths().stockTags();

        return this.settingService
          .getDocumentAsObservable<{ tags: TStockTag[] }>(globalPath)
          .pipe(
            take(1),
            map((data: { tags: TStockTag[] }) =>
              settingGlobalTagsSettings.getGlobalTagsSuccess({
                tags: data.tags || [],
                requestId,
              }),
            ),
            catchError((error) => {
              const globalError: IGlobalError = generateGlobalError(
                JSON.stringify(action),
                action.type,
                error,
                user,
              );
              if (logErrors) {
                logConsoleError(action.type, {details: globalError});
              }
              return of(
                settingGlobalTagsSettings.getGlobalTagsFailure({
                  error: globalError,
                  requestId,
                }),
              );
            }),
          );
      }),
    ),
  );

  //#endregion


  //#region EFFECT - Auto-set Section and SubSection based on Setting Reference

  /**
   * Automatically sets the section and subsection when a setting reference is selected.
   * This ensures the correct accordion sections are open in the sidebar.
   * @effect setSelectedReference$
   * @listens settingsSetSelected.setSelectedReference$
   * @dispatches settingsSetSelected.setSelectedSection
   * @dispatches settingsSetSelected.setSelectedSubSection
   * @returns settingsSetSelected.setSelectedReferenceForNavigation
   */
  setSelectedReference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsSetSelected.setSelectedReference),
      map(({ref}) => {
        let section = '';
        let subSection = '';

        // Determine section based on ref number range
        if (ref >= SettingsReferenceEnum.account && ref < SettingsReferenceEnum.application) {
          section = 'Account';
        } else if (ref === SettingsReferenceEnum.applicationEmailSettings) {
          section = 'Email';
        } else if (ref >= SettingsReferenceEnum.application && ref < SettingsReferenceEnum.feature) {
          section = 'User';
        } else if (ref >= SettingsReferenceEnum.feature) {
          section = 'Feature';
        }

        // Determine subsection for specific ref ranges
        if (ref >= SettingsReferenceEnum.featureStockRules && ref < SettingsReferenceEnum.featureLocalStorage) {
          subSection = 'Stock Settings';
        } else if (ref >= SettingsReferenceEnum.featureShelfTalkers && ref < SettingsReferenceEnum.featureStockRules) {
          subSection = 'Shelf Talkers';
        } else if (ref >= SettingsReferenceEnum.featureAutoOrder && ref < SettingsReferenceEnum.featureEmail) {
          subSection = 'Auto Ordering';
        } else if (ref >= SettingsReferenceEnum.featureStockTake) {
          subSection = 'Stock Take';
        }

        // Dispatch actions to set section and subsection
        this._store.dispatch(settingsSetSelected.setSelectedSection({ref: section}));
        this._store.dispatch(settingsSetSelected.setSelectedSubSection({ref: subSection}));

        return settingsSetSelected.setSelectedReferenceForNavigation({ref});
      }),
    ),
  );

  //#endregion

  getAllUserEmailSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        const signedInMailerRef = fsDoc(
          this._firestore,
          createUserPaths().setUserId(user.id).mailerSettings(selectedStore.storeId),
        );
        return from(getDoc(signedInMailerRef)).pipe(
          switchMap((signedInSnap) => this.readDisplayedMailerSettings(user.id, selectedStore.storeId, user.id).pipe(
            mergeMap((currentSettings: IEmailSettings) => {
              return this._collectionUsersSettingsService.getAllMailerSettingsPresence(selectedStore.storeId).pipe(
                map((loaded: IMailerSettingsPresence) => {
                  const usersEmailSettings: Record<string, IEmailSettings> = {};
                  Object.entries(loaded.settings).forEach(([userId, value]) => {
                    usersEmailSettings[userId] = sanitizeMailerSettings(value);
                  });
                  usersEmailSettings[user.id] = currentSettings;
                  return settingUserStoreMailerConfiguration.getAllUsersMailerConfigurationsSuccess({
                    usersEmailSettings,
                    usersMailerDocumentExists: {
                      ...loaded.documentExists,
                      [user.id]: signedInSnap.exists(),
                    },
                    store: selectedStore,
                  });
                }),
              );
            }),
          )),
          catchError((error) => {
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [selectedStore.storeId]);
            if (logErrors) logConsoleError(action.type, {details: globalError});
            return of(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurationsFailure({error: globalError}));
          }),
        );
      }),
    ),
  );

  testMailerConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.testMailerConfiguration),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      mergeMap(([action, user, logErrors, store]) => {
        const {authType, storeId, userId} = action;
        const payload = {storeId, userId, recipient: 'test-receive@techodactyl.co.za'};
        return from(this.copyMicrosoftSignInOntoGallixUser(userId, storeId, user?.id)).pipe(
          mergeMap(() => from(
            addDoc(fsCollection(this._firestore, path_admin_cloud_emailer_email_test()), payload),
          )),
        ).pipe(
          mergeMap((_docRef: DocumentReference) => {
            const docPath = createUserPaths().setUserId(userId).mailerSettings(storeId);
            return new Observable<IEmailTester>((subscriber) => {
              const unsubscribe = onSnapshot(
                fsDoc(this._firestore, docPath),
                (snapshot) => {
                  if (snapshot.exists()) {
                    subscriber.next(snapshot.data() as IEmailTester);
                  }
                },
                (error) => { subscriber.error(error); },
              );
              return (): void => {
                unsubscribe();
              };
            }).pipe(
              filter((doc: IEmailTester) => doc.status === 'SENT' || doc.status === 'FAILED'),
              take(1),
              mergeMap((doc: IEmailTester) => {
                const failure = emailTestFailureCopy(doc.errorMsg?.message);
                const lastTestAt = new Date().toISOString();
                const lastTestMessage = doc.status === 'FAILED' ? (doc.errorMsg?.message || failure.heading) : '';
                const modalProps: ISharedModalBasic = doc.status === 'SENT'
                  ? {
                    buttonAccept: false,
                    buttonAcceptText: '',
                    buttonClose: true,
                    buttonCloseCross: false,
                    buttonCloseText: 'Done',
                    modalTitle: 'Email test',
                    modalTitleIcon: IconsEnum.checkCircle,
                    modalTitleIconColor: IconColourEnum.green,
                    contextHeading: 'Sent',
                    contextDescription: ['A test email was sent with the saved account.'],
                    cssClass: ['microsoft-mail-result-modal-css'],
                    modalBodyScrollable: true,
                  }
                  : {
                    buttonAccept: false,
                    buttonAcceptText: '',
                    buttonClose: true,
                    buttonCloseCross: false,
                    buttonCloseText: 'Done',
                    modalTitle: 'Email test',
                    modalTitleIcon: IconsEnum.alertBox,
                    modalTitleIconColor: IconColourEnum.red,
                    contextHeading: failure.heading,
                    contextDescription: failure.details,
                    cssClass: ['microsoft-mail-result-modal-css'],
                    modalBodyScrollable: true,
                  };
                void this._modalUtils.createModal(modalProps);
                return this.settingService.updateDocumentAsObservable(
                  createUserPaths().setUserId(userId).mailerSettings(storeId),
                  {lastTestAt, lastTestAuthType: authType, lastTestMessage, lastTestStatus: doc.status, status: doc.status},
                  true,
                ).pipe(
                  map(() => settingUserStoreMailerConfiguration.testMailerConfigurationSuccess({
                    lastTestAt,
                    lastTestAuthType: authType,
                    lastTestMessage,
                    status: doc.status,
                    storeId,
                    userId,
                  })),
                );
              }),
              catchError((error) => {
                const errorModalProps: ISharedModalBasic = {
                  buttonAccept: false,
                  buttonAcceptText: '',
                  buttonClose: true,
                  buttonCloseCross: false,
                  buttonCloseText: 'Ok',
                  modalTitle: 'Email Test Error',
                  modalTitleIcon: IconsEnum.bug,
                  modalTitleIconColor: IconColourEnum.red,
                  contextHeading: 'An unexpected error occurred.',
                  contextDescription: ['We were unable to process the email test. Please try again later.'],
                };
                void this._modalUtils.createModal(errorModalProps);
                const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [store.storeId]);
                if (logErrors) logConsoleError(action.type, {details: globalError});
                return of(settingUserStoreMailerConfiguration.testMailerConfigurationFailure({error: globalError}));
              }),
            );
          }),
          catchError((error) => {
            const detail = error instanceof Error && error.message
              ? error.message
              : 'The test could not be started.';
            const lastTestAt = new Date().toISOString();
            const errorModalProps: ISharedModalBasic = {
              buttonAccept: false,
              buttonAcceptText: '',
              buttonClose: true,
              buttonCloseCross: false,
              buttonCloseText: 'Done',
              modalTitle: 'Email test',
              modalTitleIcon: IconsEnum.alertBox,
              modalTitleIconColor: IconColourEnum.red,
              contextHeading: 'Could not start the test',
              contextDescription: [
                detail,
                'The saved email account was not changed. This check did not reach Microsoft.',
              ],
              cssClass: ['microsoft-mail-result-modal-css'],
              modalBodyScrollable: true,
            };
            void this._modalUtils.createModal(errorModalProps);
            const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [store.storeId]);
            if (logErrors) logConsoleError(action.type, {details: globalError});
            return this.settingService.updateDocumentAsObservable(
              createUserPaths().setUserId(userId).mailerSettings(storeId),
              {lastTestAt, lastTestAuthType: authType, lastTestMessage: detail, lastTestStatus: 'FAILED', status: 'FAILED'},
              true,
            ).pipe(
              map(() => settingUserStoreMailerConfiguration.testMailerConfigurationSuccess({
                lastTestAt,
                lastTestAuthType: authType,
                lastTestMessage: detail,
                status: 'FAILED',
                storeId,
                userId,
              })),
              catchError(() => of(settingUserStoreMailerConfiguration.testMailerConfigurationFailure({error: globalError}))),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Saves authType on the email-keyed mailer settings document and leaves SMTP fields unchanged.
   * The Firebase uid path is not written here. Non-admins cannot read or create that document (FE-225).
   */
  setMailerAuthType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.setMailerAuthType),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
      ),
      exhaustMap(([action, user, logErrors]) => {
        const firebaseUid = this._auth.currentUser?.uid;
        const mailerPath = createUserPaths().setUserId(action.userId).mailerSettings(action.storeId);
        const mailerRef = fsDoc(this._firestore, mailerPath);
        return from(getDoc(mailerRef)).pipe(
          switchMap((mailerSnap) => {
            const documentExists = mailerSnap.exists();
            const reload$ = this.readDisplayedMailerSettings(action.userId, action.storeId, user?.id).pipe(
              map((mailSettings: IEmailSettings) => settingUserStoreMailerConfiguration.setMailerAuthTypeSuccess({
                userId: action.userId,
                storeId: action.storeId,
                mailSettings,
                mailerDocumentExists: documentExists,
              })),
            );
            if (!documentExists) {
              return reload$;
            }
            const authPatch: {authType: 'smtp' | 'microsoft365'; microsoftUserId?: string} = {
              authType: action.authType,
            };
            if (action.authType === MAIL_AUTH_MICROSOFT_365 && firebaseUid && user?.id === action.userId) {
              authPatch.microsoftUserId = firebaseUid;
            }
            return this.settingService.updateDocumentAsObservable(
              mailerPath,
              authPatch,
              true,
            ).pipe(
              switchMap(() => from(this.copyMicrosoftSignInOntoGallixUser(action.userId, action.storeId, user?.id))),
              switchMap(() => reload$),
              catchError((error) => {
                const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, error, user, [action.storeId]);
                if (logErrors) logConsoleError(action.type, {details: globalError});
                this._store.dispatch(setGlobalErrorOccurrence({error: globalError}));
                return of(settingUserStoreMailerConfiguration.setMailerAuthTypeFailure({error: globalError}));
              }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Opens Microsoft sign-in. On connected, reloads mailer settings. The refresh token is not read.
   */
  connectMicrosoftMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.connectMicrosoftMail),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        if (!user?.id || !selectedStore?.storeId) {
          const globalError: IGlobalError = generateGlobalError(
            JSON.stringify(action), action.type, new Error('Choose a store before connecting Microsoft 365.'), user,
          );
          return of(settingUserStoreMailerConfiguration.connectMicrosoftMailFailure({error: globalError}));
        }
        const gallixUserId = action.targetUserId || user.id;
        return race(
          from(this._microsoftMail.connect(selectedStore.storeId, gallixUserId, action.loginHint)),
          this.waitForSavedMicrosoftConnection(gallixUserId, selectedStore.storeId),
          timer(MICROSOFT_MAIL_WINDOW_TIMEOUT_MS).pipe(map((): IMicrosoftMailPopupResult => ({status: 'cancelled'}))),
        ).pipe(
          switchMap((result) => {
            this._microsoftMail.dismissPopup();
            return this.afterMicrosoftPopup(
              action,
              user,
              logErrors,
              selectedStore.storeId,
              result,
              true,
              gallixUserId,
            );
          }),
          catchError((error) => this.microsoftMailFailed(
            settingUserStoreMailerConfiguration.connectMicrosoftMailFailure,
            action,
            user,
            logErrors,
            selectedStore.storeId,
            error,
            'Microsoft 365 did not connect.',
          )),
        );
      }),
    ),
  );

  /**
   * Opens the Microsoft 365 admin approval page. Each person still connects their own mailbox afterwards.
   */
  requestMicrosoftAdminConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsent),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        if (!user?.id || !selectedStore?.storeId) {
          const globalError: IGlobalError = generateGlobalError(
            JSON.stringify(action), action.type, new Error('Choose a store before asking for Microsoft 365 approval.'), user,
          );
          return of(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentFailure({error: globalError}));
        }
        return race(
          from(this._microsoftMail.requestAdminConsent(selectedStore.storeId, user.id)),
          timer(MICROSOFT_MAIL_WINDOW_TIMEOUT_MS).pipe(map((): IMicrosoftMailPopupResult => ({status: 'cancelled'}))),
        ).pipe(
          switchMap((result) => {
            this._microsoftMail.dismissPopup();
            return this.afterMicrosoftPopup(
              action,
              user,
              logErrors,
              selectedStore.storeId,
              result,
              false,
              user.id,
            );
          }),
          catchError((error) => this.microsoftMailFailed(
            settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentFailure,
            action,
            user,
            logErrors,
            selectedStore.storeId,
            error,
            'Microsoft 365 admin approval did not finish.',
          )),
        );
      }),
    ),
  );

  /**
   * Removes the Microsoft connection, including a sign-in copied onto another user.
   * SMTP details already stored are left in place.
   */
  disconnectMicrosoftMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingUserStoreMailerConfiguration.disconnectMicrosoftMail),
      withLatestFrom(
        this._store.pipe(select(UserSelectors.selectUser)),
        this._store.pipe(select(UserSelectors.selectIsLogErrorsActive)),
        this._store.pipe(select(StoreSelectors.selectSelectedStore)),
      ),
      exhaustMap(([action, user, logErrors, selectedStore]) => {
        if (!user?.id || !selectedStore?.storeId) {
          const globalError: IGlobalError = generateGlobalError(
            JSON.stringify(action), action.type, new Error('Choose a store before disconnecting Microsoft 365.'), user,
          );
          return of(settingUserStoreMailerConfiguration.disconnectMicrosoftMailFailure({error: globalError}));
        }
        const targetUserId = action.targetUserId || user.id;
        return from(this.removeMicrosoftSignIn(targetUserId, selectedStore.storeId, user.id)).pipe(
          switchMap((emailKeyedDocumentExisted: boolean) => this.readDisplayedMailerSettings(targetUserId, selectedStore.storeId, user.id).pipe(
            map((mailSettings: IEmailSettings) => {
              this.showMicrosoftMailModal(
                'Disconnected',
                'Standard email is the sender again.',
                true,
              );
              return settingUserStoreMailerConfiguration.disconnectMicrosoftMailSuccess({
                userId: targetUserId,
                storeId: selectedStore.storeId,
                mailSettings: applyDisconnectedAccount(mailSettings),
                mailerDocumentExists: emailKeyedDocumentExisted,
              });
            }),
          )),
          catchError((error) => this.microsoftMailFailed(
            settingUserStoreMailerConfiguration.disconnectMicrosoftMailFailure,
            action,
            user,
            logErrors,
            selectedStore.storeId,
            error,
            'Microsoft 365 could not be disconnected.',
          )),
        );
      }),
    ),
  );

  //#region HELPER waitForActionOutcome

  /**
   * Helper method to wait for either a success or failure action with a specific request ID.
   * Used to coordinate between multiple dependent effects during initialization.
   * @param success - The success action creator to listen for
   * @param failure - The failure action creator to listen for
   * @param requestId - The request ID to filter actions by
   * @returns An observable that emits the first matching success or failure action
   */
  private waitForActionOutcome(success: ActionCreator, failure: ActionCreator, requestId: string): Observable<Object> {
    return this.actions$.pipe(
      ofType(success, failure),
      filter((a) => {
        return a['requestId'] === requestId;
      }),
      take(1),
    );
  }

  //#endregion

  //#region HELPER copyPriceBandsBetweenStores

  /**
   * Reads the source store's price bands (falling back to the global defaults
   * when the source store has no document) and overwrites the target store's
   * price-band document with them.
   * @param sourceStore - The store to copy the price bands from
   * @param targetStore - The store to overwrite the price bands on
   * @returns An observable of the bands written to the target store
   */
  private copyPriceBandsBetweenStores(sourceStore: IStore, targetStore: IStore): Observable<IPriceBand[]> {
    if (!sourceStore?.storeId || !targetStore?.storeId) {
      return from(Promise.reject(new Error('A source store and a target store are required')));
    }

    const sourcePath: string = createStorePaths().setStoreId(sourceStore.storeId).settingsPriceBands();
    const globalPath: string = createGlobalPaths().priceBands();

    return this.settingService.getDocumentAsObservable<Record<number, IPriceBand>>(sourcePath).pipe(
      take(1),
      switchMap((sourceData: Record<number, IPriceBand> | undefined): Observable<IPriceBand[]> => {
        const sourceBands: IPriceBand[] = normalizePriceBandsDocument(sourceData);
        if (sourceBands.length) {
          return of(sourceBands);
        }
        return this.settingService.getDocumentAsObservable<Record<number, IPriceBand>>(globalPath).pipe(
          take(1),
          map((globalData: Record<number, IPriceBand> | undefined): IPriceBand[] => normalizePriceBandsDocument(globalData)),
        );
      }),
      switchMap((bands: IPriceBand[]): Observable<IPriceBand[]> => {
        const targetPath: string = createStorePaths().setStoreId(targetStore.storeId).settingsPriceBands();
        return this.settingService.setDocumentOverwriteAsObservable(targetPath, toPriceBandsDocument(bands)).pipe(
          map((): IPriceBand[] => bands),
        );
      }),
    );
  }

  //#endregion

  /**
   * Deletes the Microsoft sign-in from the selected user's mailer document.
   * The emailer disconnect also runs when the signed-in user owns that sign-in.
   */
  private async removeMicrosoftSignIn(targetUserId: string, storeId: string, signedInUserId: string): Promise<boolean> {
    const firebaseUid = this._auth.currentUser?.uid;
    const targetRef = fsDoc(this._firestore, createUserPaths().setUserId(targetUserId).mailerSettings(storeId));
    const targetSnap = await getDoc(targetRef);
    const emailKeyedDocumentExisted = targetSnap.exists();
    const microsoftUserId = targetSnap.data()?.['microsoftUserId'];
    const signedInUserSelected = targetUserId === signedInUserId;
    const currentUserOwnsSignIn = typeof microsoftUserId === 'string'
      && !!firebaseUid
      && microsoftUserId === firebaseUid;
    if (signedInUserSelected) {
      try {
        await this._microsoftMail.disconnect(storeId, targetUserId);
      } catch {
        // The email-keyed document is cleared below.
      }
    }
    if (targetSnap.exists()) {
      await setDoc(targetRef, {
        authType: MAIL_AUTH_SMTP,
        microsoft: deleteField(),
        microsoftUserId: deleteField(),
      }, {merge: true});
    }
    if ((signedInUserSelected || currentUserOwnsSignIn) && firebaseUid && firebaseUid !== targetUserId) {
      const uidRef = fsDoc(this._firestore, createUserPaths().setUserId(firebaseUid).mailerSettings(storeId));
      const uidSnap = await getDoc(uidRef);
      if (!uidSnap.exists()) {
        return emailKeyedDocumentExisted;
      }
      const uidPatch: {
        microsoft: ReturnType<typeof deleteField>;
        microsoftUserId: ReturnType<typeof deleteField>;
        authType?: typeof MAIL_AUTH_SMTP;
      } = {
        microsoft: deleteField(),
        microsoftUserId: deleteField(),
      };
      if (signedInUserSelected) {
        uidPatch.authType = MAIL_AUTH_SMTP;
      }
      try {
        await setDoc(uidRef, uidPatch, {merge: true});
      } catch {
        // Managers cannot update users/{uid}. The email-keyed document is already Standard.
      }
    }
    return emailKeyedDocumentExisted;
  }

  /**
   * Copies the encrypted Microsoft sign-in onto the signed-in Gallix user's settings.
   * Other users are left unchanged. SMTP fields stay. The token is not kept in app state.
   */
  private async copyMicrosoftSignInOntoGallixUser(
    gallixUserId: string,
    storeId: string,
    signedInUserId: string | undefined,
  ): Promise<void> {
    const firebaseUid = this._auth.currentUser?.uid;
    if (!firebaseUid || !signedInUserId || gallixUserId !== signedInUserId || firebaseUid === gallixUserId) {
      return;
    }
    const gallixRef = fsDoc(this._firestore, createUserPaths().setUserId(gallixUserId).mailerSettings(storeId));
    const gallixSnap = await getDoc(gallixRef);
    if (gallixSnap.data()?.['authType'] !== MAIL_AUTH_MICROSOFT_365) {
      return;
    }
    const uidRef = fsDoc(this._firestore, createUserPaths().setUserId(firebaseUid).mailerSettings(storeId));
    let uidSnap: Awaited<ReturnType<typeof getDoc>> | undefined;
    try {
      uidSnap = await getDoc(uidRef);
    } catch {
      return;
    }
    const microsoft = uidSnap.data()?.['microsoft'];
    if (!microsoft || typeof microsoft !== 'object') {
      return;
    }
    const refreshToken = (microsoft as {refreshToken?: unknown}).refreshToken;
    if (typeof refreshToken !== 'string' || refreshToken.length === 0) {
      return;
    }
    await setDoc(gallixRef, {
      authType: MAIL_AUTH_MICROSOFT_365,
      microsoft,
      microsoftUserId: firebaseUid,
    }, {merge: true});
  }

  /**
   * Reads one user's email-keyed mailer document.
   * A Firebase-uid mailbox supplies sign-in details only when that read is allowed.
   * It does not replace authType. A denied uid read must not hide the email-keyed SMTP settings (FE-225).
   */
  private readDisplayedMailerSettings(
    appUserId: string,
    storeId: string,
    signedInUserId: string | undefined,
  ): Observable<IEmailSettings> {
    const appPath = createUserPaths().setUserId(appUserId).mailerSettings(storeId);
    return this.settingService.getDocumentAsObservable<unknown>(appPath).pipe(
      switchMap((raw: unknown) => {
        const base = sanitizeMailerSettings(raw);
        const firebaseUid = this._auth.currentUser?.uid;
        if (!firebaseUid || !signedInUserId || appUserId !== signedInUserId || firebaseUid === appUserId) {
          return of(base);
        }
        const uidPath = createUserPaths().setUserId(firebaseUid).mailerSettings(storeId);
        return this.settingService.getDocumentAsObservable<unknown>(uidPath).pipe(
          map((uidRaw: unknown) => overlayMicrosoftAccount(base, sanitizeMailerSettings(uidRaw))),
          catchError(() => of(base)),
        );
      }),
    );
  }

  /**
   * The Microsoft window cannot reliably tell this page that sign-in finished.
   * The emailer saves the mailbox first, so this watches that document.
   */
  private waitForSavedMicrosoftConnection(appUserId: string, storeId: string): Observable<IMicrosoftMailPopupResult> {
    return this.readDisplayedMailerSettings(appUserId, storeId, appUserId).pipe(
      take(1),
      switchMap((before: IEmailSettings) => timer(2000, 2000).pipe(
        switchMap(() => this.readDisplayedMailerSettings(appUserId, storeId, appUserId)),
        filter((settings: IEmailSettings) => this.microsoftConnectionAppeared(before, settings)),
        take(1),
        map((settings: IEmailSettings): IMicrosoftMailPopupResult => ({
          status: 'connected',
          email: settings.connectedMailbox || settings.email,
        })),
      )),
    );
  }

  /**
   * True when the emailer has stored a Microsoft mailbox that was not there before this sign-in.
   */
  private microsoftConnectionAppeared(before: IEmailSettings, after: IEmailSettings): boolean {
    const gainedToken = after.hasMicrosoftRefreshToken === true && before.hasMicrosoftRefreshToken !== true;
    const becameMicrosoft = after.authType === MAIL_AUTH_MICROSOFT_365 && before.authType !== MAIL_AUTH_MICROSOFT_365;
    return gainedToken || becameMicrosoft;
  }

  /**
   * Handles the emailer popup result for connect and admin approval.
   */
  private afterMicrosoftPopup(
    action: {type: string},
    user: IUser,
    logErrors: boolean,
    storeId: string,
    result: {status: string; email?: string; message?: string},
    connectOnSuccess: boolean,
    targetUserId: string,
  ): Observable<Action> {
    if (result.status === 'cancelled') {
      return of(settingUserStoreMailerConfiguration.microsoftMailCancelled());
    }
    if (result.status === 'error') {
      const message = result.message || 'Microsoft 365 did not finish.';
      this.showMicrosoftMailModal('Not connected', message, false);
      const globalError: IGlobalError = generateGlobalError(JSON.stringify(action), action.type, new Error(message), user, [storeId]);
      if (logErrors) logConsoleError(action.type, {details: globalError});
      return of(connectOnSuccess
        ? settingUserStoreMailerConfiguration.connectMicrosoftMailFailure({error: globalError})
        : settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentFailure({error: globalError}));
    }
    if (result.status === 'admin-consented') {
      this.showMicrosoftMailModal(
        'Approved',
        'Each person still connects their own mailbox.',
        true,
      );
      return of(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentSuccess());
    }
    if (!connectOnSuccess) {
      this.showMicrosoftMailModal('Finished', 'The Microsoft window closed.', true);
      return of(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentSuccess());
    }
    const mailerRef = fsDoc(this._firestore, createUserPaths().setUserId(targetUserId).mailerSettings(storeId));
    return from(getDoc(mailerRef)).pipe(
      switchMap((mailerSnap) => this.readDisplayedMailerSettings(targetUserId, storeId, user.id).pipe(
        map((mailSettings: IEmailSettings) => {
          const mailbox = result.email || mailSettings.connectedMailbox || mailSettings.email;
          this.showMicrosoftMailModal(
            'Connected',
            mailbox || 'The mailbox is ready to send.',
            true,
          );
          return settingUserStoreMailerConfiguration.connectMicrosoftMailSuccess({
            userId: targetUserId,
            storeId,
            mailSettings: applyConnectedMailbox(mailSettings, result.email),
            mailerDocumentExists: mailerSnap.exists(),
          });
        }),
      )),
    );
  }

  /**
   * Shows a modal and returns the matching failure action.
   */
  private microsoftMailFailed(
    failure: (props: {error: IGlobalError}) => Action,
    action: {type: string},
    user: IUser,
    logErrors: boolean,
    storeId: string,
    error: unknown,
    fallback: string,
  ): Observable<Action> {
    const message = error instanceof Error ? error.message : fallback;
    this.showMicrosoftMailModal('Not connected', message, false);
    const globalError: IGlobalError = generateGlobalError(
      JSON.stringify(action),
      action.type,
      error as IGlobalError,
      user,
      [storeId],
    );
    if (logErrors) logConsoleError(action.type, {details: globalError});
    return of(failure({error: globalError}));
  }

  /**
   * Shows a short result dialog for Microsoft 365 connect, approval, or disconnect.
   */
  private showMicrosoftMailModal(heading: string, message: string, success: boolean): void {
    const modalProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Done',
      modalTitle: 'Microsoft 365',
      modalTitleIcon: success ? IconsEnum.checkCircle : IconsEnum.alertBox,
      modalTitleIconColor: success ? IconColourEnum.green : IconColourEnum.red,
      contextHeading: heading,
      contextDescription: message ? [message] : [],
      cssClass: ['microsoft-mail-result-modal-css'],
      modalBodyScrollable: true,
    };
    void this._modalUtils.createModal(modalProps);
  }

}
